import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faRobot, faUser, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import owaspWstgService from '../../services/owaspWstgService';
import webFindingService from '../../services/webFindingService';

// WSTG categories
const WSTG_CATEGORIES = [
  { code: 'INFO', label: 'Information Gathering' },
  { code: 'CONF', label: 'Configuration & Deployment' },
  { code: 'IDNT', label: 'Identity Management' },
  { code: 'ATHN', label: 'Authentication' },
  { code: 'ATHZ', label: 'Authorization' },
  { code: 'SESS', label: 'Session Management' },
  { code: 'INPV', label: 'Input Validation' },
  { code: 'ERRH', label: 'Error Handling' },
  { code: 'CRYP', label: 'Cryptography' },
  { code: 'BUSL', label: 'Business Logic' },
  { code: 'CLNT', label: 'Client-side Testing' },
  { code: 'APIT', label: 'API Testing' },
];

const ExposIQCoverageOverlay = ({ webTarget, isDarkMode }) => {
  const { t } = useLanguage();
  const [wstgTests, setWstgTests] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [tests, targetFindings] = await Promise.all([
          owaspWstgService.getAll().catch(() => []),
          webFindingService.getAll({ webTargetId: webTarget._id }).catch(() => [])
        ]);
        setWstgTests(Array.isArray(tests) ? tests : []);
        setFindings(Array.isArray(targetFindings) ? targetFindings : []);
      } finally {
        setLoading(false);
      }
    };
    if (webTarget?._id) loadData();
  }, [webTarget]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <FontAwesomeIcon icon={faSpinner} spin className="text-teal-500" />
      </div>
    );
  }

  // Build coverage map from findings
  const coverageMap = {};
  findings.forEach(f => {
    if (!f.wstgId) return;
    const key = f.wstgId;
    if (!coverageMap[key]) coverageMap[key] = { exposiq: false, manual: false };
    if (f.source === 'exposiq_import') {
      coverageMap[key].exposiq = true;
    } else {
      coverageMap[key].manual = true;
    }
  });

  // Count by status
  let coveredExposiq = 0, coveredManual = 0, coveredBoth = 0, notTested = 0;
  const testsByCategory = {};

  wstgTests.forEach(test => {
    const testId = test.testId || test.id;
    if (!testId) return;
    const catCode = testId.split('-')[1];
    if (!testsByCategory[catCode]) testsByCategory[catCode] = [];

    const coverage = coverageMap[testId];
    let status = 'not_tested';
    if (coverage?.exposiq && coverage?.manual) {
      status = 'both';
      coveredBoth++;
    } else if (coverage?.exposiq) {
      status = 'exposiq';
      coveredExposiq++;
    } else if (coverage?.manual) {
      status = 'manual';
      coveredManual++;
    } else {
      notTested++;
    }

    testsByCategory[catCode].push({ ...test, testId, status });
  });

  const totalTests = wstgTests.length || 1;
  const totalCovered = coveredExposiq + coveredManual + coveredBoth;

  return (
    <div className="p-4 space-y-4 overflow-auto h-full">
      {/* Summary Bar */}
      <div className={`rounded-lg border p-4 ${cardBg}`}>
        <h3 className={`font-semibold mb-3 flex items-center gap-2 ${textPrimary}`}>
          <FontAwesomeIcon icon={faShieldAlt} className="text-teal-500" />
          {t('exposiq.coverageTitle', 'WSTG Coverage')}
        </h3>

        {/* Coverage bar */}
        <div className={`w-full rounded-full h-3 mb-3 flex overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          {coveredBoth > 0 && (
            <div className="bg-purple-500 h-3" style={{ width: `${(coveredBoth / totalTests) * 100}%` }} />
          )}
          {coveredExposiq > 0 && (
            <div className="bg-green-500 h-3" style={{ width: `${(coveredExposiq / totalTests) * 100}%` }} />
          )}
          {coveredManual > 0 && (
            <div className="bg-blue-500 h-3" style={{ width: `${(coveredManual / totalTests) * 100}%` }} />
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-green-500 inline-block" />
            <span className={textSecondary}>
              <FontAwesomeIcon icon={faRobot} className="mr-1" />
              {t('exposiq.coveredByExposiq', 'ExposIQ')} ({coveredExposiq})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" />
            <span className={textSecondary}>
              <FontAwesomeIcon icon={faUser} className="mr-1" />
              {t('exposiq.coveredByManual', 'Manual')} ({coveredManual})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-purple-500 inline-block" />
            <span className={textSecondary}>{t('exposiq.coveredByBoth', 'Both')} ({coveredBoth})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm inline-block ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            <span className={textSecondary}>{t('exposiq.notTested', 'Not Tested')} ({notTested})</span>
          </div>
        </div>

        <div className={`mt-2 text-sm ${textSecondary}`}>
          {totalCovered} / {wstgTests.length} tests covered ({Math.round((totalCovered / totalTests) * 100)}%)
        </div>
      </div>

      {/* Category breakdown */}
      {WSTG_CATEGORIES.map(cat => {
        const tests = testsByCategory[cat.code] || [];
        if (tests.length === 0) return null;

        return (
          <div key={cat.code} className={`rounded-lg border p-3 ${cardBg}`}>
            <h4 className={`text-sm font-medium mb-2 ${textPrimary}`}>
              WSTG-{cat.code}: {cat.label}
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {tests.map(test => (
                <div
                  key={test.testId}
                  title={`${test.testId}: ${test.name || test.testName || ''}`}
                  className={`w-7 h-7 rounded flex items-center justify-center text-xs font-mono cursor-default ${
                    test.status === 'both' ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' :
                    test.status === 'exposiq' ? 'bg-green-500/30 text-green-300 border border-green-500/50' :
                    test.status === 'manual' ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50' :
                    isDarkMode ? 'bg-gray-700 text-gray-500 border border-gray-600' : 'bg-gray-100 text-gray-400 border border-gray-200'
                  }`}
                >
                  {test.testId.split('-').pop()}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExposIQCoverageOverlay;
